/**
 * Centralized logger with ring buffer for bug reports.
 *
 * Usage:
 *   import { logger } from '@/lib/logger';
 *   logger.info('[Room]', 'Connected', { participants: 3 });
 *   logger.error('[API]', 'Failed to fetch', error);
 *
 * Behavior:
 *   - All levels write to the ring buffer (available via getLogs())
 *   - error/warn/info always emit to console (PostHog session replay records the
 *     console, so info must reach it in production too)
 *   - debug emits to console when localStorage.debug === 'true'
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  tag: string;
  message: string;
  data?: unknown;
}

export type LogSink = (entry: LogEntry) => void;

const MAX_ENTRIES = 200;
const buffer: LogEntry[] = [];
let sink: LogSink | undefined;

const isDev = typeof process !== 'undefined' && process.env.NODE_ENV === 'development';

function isDebugEnabled(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem('debug') === 'true';
  } catch {
    return false;
  }
}

function formatArgs(args: unknown[]): { tag: string; message: string; data?: unknown } {
  const first = args[0];
  let tag = '';
  let rest = args;

  // Extract [Tag] prefix if present
  if (typeof first === 'string' && first.startsWith('[') && first.includes(']')) {
    tag = first;
    rest = args.slice(1);
  }

  const messageParts: string[] = [];
  const objects: unknown[] = [];

  for (const arg of rest) {
    if (arg instanceof Error) {
      messageParts.push(arg.message);
      objects.push({ stack: arg.stack });
    } else if (typeof arg === 'object' && arg !== null) {
      objects.push(arg);
    } else {
      messageParts.push(String(arg));
    }
  }

  const data = objects.length === 0 ? undefined : objects.length === 1 ? objects[0] : objects;
  return { tag, message: messageParts.join(' '), data };
}

function write(level: LogLevel, args: unknown[]) {
  const { tag, message, data } = formatArgs(args);
  const entry: LogEntry = {
    timestamp: new Date().toISOString(),
    level,
    tag,
    message,
    ...(data !== undefined && { data }),
  };

  buffer.push(entry);
  if (buffer.length > MAX_ENTRIES) buffer.shift();

  if (sink && level !== 'debug') {
    try {
      sink(entry);
    } catch {
      // a broken sink must never break the caller
    }
  }

  // Console output rules
  const prefix = tag ? `${tag} ` : '';
  const consoleArgs = data !== undefined ? [`${prefix}${message}`, data] : [`${prefix}${message}`];

  switch (level) {
    case 'error':
      console.error(...consoleArgs);
      break;
    case 'warn':
      console.warn(...consoleArgs);
      break;
    case 'info':
      console.log(...consoleArgs);
      break;
    case 'debug':
      if (isDev || isDebugEnabled()) console.debug(...consoleArgs);
      break;
  }
}

export const logger = {
  debug: (...args: unknown[]) => write('debug', args),
  info: (...args: unknown[]) => write('info', args),
  warn: (...args: unknown[]) => write('warn', args),
  error: (...args: unknown[]) => write('error', args),

  /** Get all buffered log entries (for bug reports) */
  getLogs: (): LogEntry[] => [...buffer],

  /** Get logs filtered by level */
  getLogsByLevel: (level: LogLevel): LogEntry[] => buffer.filter((e) => e.level === level),

  /** Get logs as formatted strings (for bug report payload) */
  getLogsFormatted: (): string[] =>
    buffer.map(
      (e) =>
        `[${e.timestamp}] ${e.level.toUpperCase()} ${e.tag} ${e.message}${e.data ? ' ' + JSON.stringify(e.data) : ''}`,
    ),

  /** Clear the buffer */
  clear: () => {
    buffer.length = 0;
  },

  /** Forward info/warn/error entries to an external destination (e.g. Datadog) */
  setSink: (next: LogSink | undefined) => {
    sink = next;
  },
};

// Capture unhandled errors into the logger
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    logger.error('[Unhandled]', `${event.message} at ${event.filename}:${event.lineno}`);
  });

  window.addEventListener('unhandledrejection', (event) => {
    logger.error('[UnhandledRejection]', String(event.reason));
  });
}
